import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdCheck, MdKeyboardArrowDown, MdLockOutline, MdOutlineArrowBack, MdOutlineChatBubbleOutline, MdOutlineSchedule } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import Loader from 'components/Loader';
import {
    type EssaySummary,
    type EssayVersionSummary,
    type EssayVersionDetail,
    type EssayComment,
    type DraftState,
    draftState,
    promptNumber,
    wordCount,
} from 'global/essays';

import './index.css';

const SAVE_DEBOUNCE_MS = 1500;
const TARGET_WORDS = 650;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'locked';

function shortDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function shortDateTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Scroll the WINDOW so el sits mid-viewport. el.scrollIntoView would also
// scroll overflow:hidden ancestors (the essay page cards), visually shoving
// their content upward with no scrollbar to recover.
function scrollWindowTo(el: Element | null) {
    if (!el) return;
    // Find the ancestor that actually scrolls (overflow-y auto/scroll with
    // overflowing content) — the app shell may scroll a container instead of
    // the window. overflow:hidden ancestors are deliberately skipped.
    let scroller: Element | null = null;
    for (let p = el.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1) { scroller = p; break; }
    }
    const r = el.getBoundingClientRect();
    if (scroller) {
        const c = scroller.getBoundingClientRect();
        scroller.scrollTo({
            top: scroller.scrollTop + (r.top - c.top) + r.height / 2 - scroller.clientHeight / 2,
            behavior: 'smooth',
        });
    } else {
        window.scrollTo({ top: window.scrollY + r.top + r.height / 2 - window.innerHeight / 2, behavior: 'smooth' });
    }
}

// Split content into range/plain segments. Overlapping or out-of-bounds ranges are
// skipped (first-wins) so offsets always stay valid. Shared by the read-only comment
// highlights and the editing backdrop's needs-edit marks.
function segmentByRanges<T extends { start: number; end: number }>(
    content: string,
    ranges: T[],
): Array<{ text: string; range?: T }> {
    const valid = [...ranges]
        .filter(r => r.end > r.start && r.start >= 0 && r.end <= content.length)
        .sort((a, b) => a.start - b.start);
    const segs: Array<{ text: string; range?: T }> = [];
    let pos = 0;
    for (const r of valid) {
        if (r.start < pos) continue;
        if (r.start > pos) segs.push({ text: content.slice(pos, r.start) });
        segs.push({ text: content.slice(r.start, r.end), range: r });
        pos = r.end;
    }
    if (pos < content.length) segs.push({ text: content.slice(pos) });
    return segs;
}

interface Segment { text: string; comment?: EssayComment; }
function buildSegments(content: string, comments: EssayComment[]): Segment[] {
    return segmentByRanges(
        content,
        comments.map(c => ({ start: c.anchorStart, end: c.anchorEnd, comment: c })),
    ).map(s => ({ text: s.text, comment: s.range?.comment }));
}

function EssayEditor() {
    const navigate = useNavigate();
    const { essayId } = useParams<{ essayId: string }>();
    const { organization } = useAppSelector(state => state.organization);

    const [essay, setEssay] = useState<EssaySummary | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [activeVersionId, setActiveVersionId] = useState<number | null>(null);
    const [version, setVersion] = useState<EssayVersionDetail | null>(null);
    const [comments, setComments] = useState<EssayComment[]>([]);
    // Hover linking: comment card <-> its highlighted passage.
    const [hoverCommentId, setHoverCommentId] = useState<number | null>(null);
    const [content, setContent] = useState('');
    const [status, setStatus] = useState<SaveStatus>('idle');
    const [busy, setBusy] = useState(false);          // creating a new draft
    const [submitting, setSubmitting] = useState(false);
    const [draftError, setDraftError] = useState<string | null>(null);

    // Carry-forward feedback: the prior reviewed draft's published comments + content.
    const [priorComments, setPriorComments] = useState<EssayComment[]>([]);
    const [priorVersion, setPriorVersion] = useState<EssayVersionSummary | null>(null);
    const [priorContent, setPriorContent] = useState('');
    const [compareOn, setCompareOn] = useState(false);

    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const savedContentRef = useRef('');
    const taRef = useRef<HTMLTextAreaElement | null>(null);
    const backdropRef = useRef<HTMLDivElement | null>(null);

    // Draft picker dropdown (replaces the old left drafts rail).
    const [draftsOpen, setDraftsOpen] = useState(false);
    const draftPickRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!draftsOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (draftPickRef.current && !draftPickRef.current.contains(e.target as Node)) setDraftsOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [draftsOpen]);

    const state: DraftState = version ? draftState(version) : 'editing';
    // A revision that's byte-for-byte the reviewed draft has nothing to review —
    // submit stays disabled until the student actually changes something.
    const unchangedFromPrior = !!priorContent && content.trim() === priorContent.trim();
    // An editing draft is only truly editable if it isn't locked. A version locks once
    // it has any review (e.g. a tutor-only AI run), so guard against a typeable box that
    // would 409 on save.
    const editable = !!version && state === 'editing' && !version.isLocked;

    const loadEssay = useCallback((selectVersionId?: number) => {
        if (!organization.urlSlug || !essayId) return;
        axios.get(`fillapp/student/essays/${organization.urlSlug}/`)
            .then(res => {
                const found = (res.data.essays as EssaySummary[]).find(e => String(e.id) === essayId);
                if (!found) { setLoadError('Essay not found.'); return; }
                setEssay(found);
                const versions = found.versions || [];
                const target = selectVersionId
                    ?? activeVersionId
                    ?? versions[versions.length - 1]?.id
                    ?? null;
                setActiveVersionId(target);
            })
            .catch(err => setLoadError(err?.response?.data?.message || 'Failed to load essay.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization.urlSlug, essayId]);

    useEffect(() => { loadEssay(); /* eslint-disable-next-line */ }, [organization.urlSlug, essayId]);

    // Load the active version's content + (published) comments.
    useEffect(() => {
        if (!activeVersionId) return;
        let cancelled = false;
        setVersion(null);
        axios.get(`fillapp/student/version/${activeVersionId}/`)
            .then(res => {
                if (cancelled) return;
                const v: EssayVersionDetail = res.data.version;
                setVersion(v);
                setComments(res.data.comments || []);
                setContent(v.content);
                savedContentRef.current = v.content;
                setStatus(v.isLocked ? 'locked' : 'idle');
            })
            .catch(err => { if (!cancelled) setLoadError(err?.response?.data?.message || 'Failed to load draft.'); });
        return () => { cancelled = true; };
    }, [activeVersionId]);

    // When editing, surface the most recent reviewed draft's feedback as a checklist.
    useEffect(() => {
        if (!essay || !version || state !== 'editing') { setPriorComments([]); setPriorVersion(null); setPriorContent(''); setCompareOn(false); return; }
        const versions = essay.versions || [];
        const idx = versions.findIndex(v => v.id === version.id);
        const prior = [...versions.slice(0, idx)].reverse().find(v => v.hasPublishedComments);
        if (!prior) { setPriorComments([]); setPriorVersion(null); setPriorContent(''); setCompareOn(false); return; }
        let cancelled = false;
        setPriorVersion(prior);
        axios.get(`fillapp/student/version/${prior.id}/`)
            .then(res => {
                if (cancelled) return;
                const prior: EssayComment[] = res.data.comments || [];
                setPriorComments(prior);
                setPriorContent(res.data.version?.content || '');
            })
            .catch(() => { if (!cancelled) { setPriorComments([]); setPriorContent(''); } });
        return () => { cancelled = true; };
    }, [essay, version, state]);

    const doSave = useCallback((text: string) => {
        if (!activeVersionId) return;
        setStatus('saving');
        axios.post(`fillapp/student/version/${activeVersionId}/save/`, { content: text })
            .then(() => { savedContentRef.current = text; setStatus('saved'); })
            .catch(err => {
                if (err?.response?.status === 409) {
                    // The draft was locked (tutor started reviewing) while we were editing.
                    // Fail safe: keep the student's typed `text` in state (do NOT overwrite
                    // it) so "New draft" can carry it forward, reconcile lock/comments from
                    // the server, and tell them their latest edits weren't saved.
                    setStatus('locked');
                    setDraftError('This draft was locked because your tutor started reviewing it. '
                        + 'Your latest edits weren’t saved — start a new draft to keep writing '
                        + '(your text has been carried over).');
                    axios.get(`fillapp/student/version/${activeVersionId}/`)
                        .then(res => {
                            setVersion({ ...res.data.version, isLocked: true });
                            setComments(res.data.comments || []);
                            // Intentionally do NOT setContent — preserve the typed text.
                        })
                        .catch(() => setVersion(v => (v ? { ...v, isLocked: true } : v)));
                } else { setStatus('error'); }
            });
    }, [activeVersionId]);

    const onChangeContent = (text: string) => {
        setContent(text);
        if (!editable) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            if (text !== savedContentRef.current) doSave(text);
        }, SAVE_DEBOUNCE_MS);
    };

    const flushSave = useCallback(() => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        if (editable && content !== savedContentRef.current) doSave(content);
    }, [editable, content, doSave]);

    const flushRef = useRef(flushSave);
    useEffect(() => { flushRef.current = flushSave; });
    useEffect(() => () => { flushRef.current(); }, []);

    const switchVersion = (id: number) => {
        if (id === activeVersionId) return;
        flushSave();
        setActiveVersionId(id);
    };

    const newDraft = () => {
        if (busy) return;
        setBusy(true);
        setDraftError(null);
        flushSave();
        axios.post(`fillapp/student/essay/${essayId}/version/`, { content })
            .then(res => loadEssay(res.data.version.id))
            .catch(err => setDraftError(err?.response?.data?.message || 'Could not create a new draft.'))
            .finally(() => setBusy(false));
    };

    const submitForReview = () => {
        if (!activeVersionId || submitting) return;
        setSubmitting(true);
        setDraftError(null);
        flushSave();
        axios.post(`fillapp/student/version/${activeVersionId}/submit/`)
            .then(res => { setVersion(res.data.version); loadEssay(activeVersionId); })
            .catch(err => setDraftError(err?.response?.data?.message || 'Could not submit for review.'))
            .finally(() => setSubmitting(false));
    };

    const withdraw = () => {
        if (!activeVersionId || submitting) return;
        setSubmitting(true);
        setDraftError(null);
        axios.post(`fillapp/student/version/${activeVersionId}/withdraw/`)
            .then(res => { setVersion(res.data.version); loadEssay(activeVersionId); })
            .catch(err => setDraftError(err?.response?.data?.message || 'Could not withdraw.'))
            .finally(() => setSubmitting(false));
    };

    // Click a comment card -> scroll the window to its passage: the editing
    // backdrop mark ([data-hl]) or a rendered highlight span ([data-cid]).
    const scrollToPassage = (c: EssayComment) => {
        if (c.isGeneral || !c.anchorText) return;
        scrollWindowTo(document.querySelector(`[data-hl="${c.id}"], [data-cid="${c.id}"]`));
    };

    // Hover over marked text in the EDITOR: the textarea sits above the backdrop
    // and eats mouse events, so hit-test the pointer against the marks' client
    // rects instead of relying on enter/leave.
    const onEditorMouseMove = (e: React.MouseEvent) => {
        const root = backdropRef.current;
        if (!root) return;
        let found: number | null = null;
        const marks = root.querySelectorAll<HTMLElement>('[data-hl]');
        for (const el of Array.from(marks)) {
            for (const r of Array.from(el.getClientRects())) {
                if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
                    found = Number(el.dataset.hl);
                    break;
                }
            }
            if (found != null) break;
        }
        setHoverCommentId(found);
    };

    // Needs-edit spans: each prior comment whose flagged text still exists
    // verbatim in this draft (new drafts start as a copy, so indexOf relocates it).
    // Resolution is submit-driven: submitting this draft resolves everything, so
    // there are no per-comment checkboxes anymore.
    const backdropRanges = useMemo(() => {
        const out: Array<{ start: number; end: number; id: number }> = [];
        for (const c of priorComments) {
            if (c.isGeneral || !c.anchorText) continue;
            const i = content.indexOf(c.anchorText);
            if (i >= 0) out.push({ start: i, end: i + c.anchorText.length, id: c.id });
        }
        return out;
    }, [content, priorComments]);
    const backdropSegs = useMemo(
        () => segmentByRanges(content, backdropRanges),
        [content, backdropRanges],
    );

    // Autosize the textarea to its content so it never scrolls internally — the
    // backdrop then lives in the same page flow (no scroll-sync) and line wrapping is
    // identical (no scrollbar stealing width). Height depends on the WIDTH the text
    // wraps at, so it must recompute when compare mode halves the column and on any
    // element resize (window resize, panel changes) — otherwise the stale height
    // clips the essay (overflow: hidden).
    useLayoutEffect(() => {
        const ta = taRef.current;
        if (!ta || !editable) return;
        const fit = () => {
            ta.style.height = 'auto';
            ta.style.height = `${ta.scrollHeight}px`;
        };
        fit();
        const ro = new ResizeObserver(entries => {
            // Only react to WIDTH changes (height changes are our own writes).
            for (const e of entries) {
                if (Math.abs(e.contentRect.width - (ta.dataset.lastW ? Number(ta.dataset.lastW) : -1)) > 0.5) {
                    ta.dataset.lastW = String(e.contentRect.width);
                    fit();
                }
            }
        });
        ro.observe(ta);
        return () => ro.disconnect();
    }, [content, editable, compareOn]);

    if (!essay && !loadError) return <Loader />;
    if (loadError) return <div className='layout'><div className='essayEd__error'>{loadError}</div></div>;

    const versions = essay!.versions || [];
    const latest = versions[versions.length - 1];
    // A new draft can only be branched once the latest has been reviewed by the tutor
    // (not while it's still Editing or In review / awaiting review).
    const canAddDraft = !!latest && draftState(latest) === 'reviewed';
    const count = wordCount(content);
    const isSupp = essay!.essayType === 'supplement';
    // Personal statements target the Common App's 650; supplements use the
    // student-entered limit (advisory — over-limit is flagged, never blocked)
    // or no target at all.
    const target = isSupp ? essay!.wordLimit : TARGET_WORDS;
    const pct = target ? Math.min(100, Math.round((count / target) * 1000) / 10) : 0;
    const overLimit = !!target && count > target;
    const num = isSupp ? null : promptNumber(essay!.prompt);

    return (
        <div className='essayEd layout'>
            <Helmet><title>{essay!.title} - Essay Review - {organization.name}</title></Helmet>

            <button className='pageBack' type='button' onClick={() => { flushSave(); navigate('/college-apps?tab=essays'); }}>
                <MdOutlineArrowBack />
                Back to dashboard
            </button>

            <div className='essayEd__top'>
                <div>
                    <div className='essayEd__eyebrow'>
                        {isSupp
                            ? `College supplement${essay!.collegeName ? ` · ${essay!.collegeName}` : ''}`
                            : `Common Application${num ? ` · Prompt ${num}` : ''}`}
                    </div>
                    <h1 className='essayEd__title'>{essay!.title}</h1>
                </div>
            </div>

            {essay!.prompt && (
                <div className='essayEd__ribbon'>
                    {isSupp
                        ? <span className='essayEd__ribbonTag'>SUPPLEMENT</span>
                        : num && <span className='essayEd__ribbonTag'>PROMPT {num}</span>}
                    <p>{essay!.prompt}</p>
                </div>
            )}

            <div className='essayEd__shell'>
                {/* canvas */}
                <div>
                    {!version ? <Loader /> : (
                        <>
                            {state === 'editing' && (
                                <div className='essayEd__submitbar'>
                                    <span className='essayEd__submitHint'>
                                        Your tutor can see your drafts anytime. Submitting tells them <b>this draft</b> is ready for review.
                                    </span>
                                    <span className='essayEd__submitActions'>
                                        {priorVersion && priorContent && (
                                            <button className='essayEd__btn essayEd__btn--ghost' onClick={() => setCompareOn(v => !v)}>
                                                {compareOn ? 'Exit compare' : `Compare with ${priorVersion.label}`}
                                            </button>
                                        )}
                                        <button
                                            className='essayEd__btn essayEd__btn--primary'
                                            onClick={submitForReview}
                                            disabled={submitting || content.trim() === '' || unchangedFromPrior}
                                            title={content.trim() === ''
                                                ? 'Write your essay before submitting it for review'
                                                : unchangedFromPrior
                                                    ? `Make changes before submitting — this draft is identical to ${priorVersion?.label ?? 'the reviewed draft'}`
                                                    : undefined}
                                        >
                                            {submitting ? 'Submitting…' : `Submit ${version.label} for review`}
                                        </button>
                                    </span>
                                </div>
                            )}

                            {state === 'in_review' && (
                                <div className='essayEd__lockbar essayEd__lockbar--info'>
                                    <span className='essayEd__lockIc'><MdOutlineSchedule size={16} /></span>
                                    <p>
                                        <b>{version.label} is in review.</b> Your tutor has it now — their notes will appear here
                                        when they're done. Need to change something first?{' '}
                                        <a onClick={withdraw} className='essayEd__link'>Withdraw submission</a> to keep editing.
                                    </p>
                                </div>
                            )}

                            {state === 'reviewed' && (
                                <div className='essayEd__reviewbar'>
                                    <span className='essayEd__reviewIc'><MdCheck size={17} /></span>
                                    <div className='essayEd__reviewBody'>
                                        <b>Your tutor reviewed {version.label}</b>
                                        <p>
                                            {comments.length > 0 && (
                                                <>{comments.length} comment{comments.length === 1 ? '' : 's'}
                                                {comments.some(c => !c.isGeneral) ? ' — highlighted in the essay below.' : '.'}{' '}</>
                                            )}
                                            {canAddDraft
                                                ? 'Read the feedback, then revise in a fresh draft. This one stays as-is so each comment keeps pointing at the exact words it\'s about.'
                                                : latest && latest.id !== version.id
                                                    ? `You've already started ${latest.label} — keep revising there.`
                                                    : ''}
                                        </p>
                                    </div>
                                    {canAddDraft ? (
                                        <button className='essayEd__btn essayEd__btn--primary' onClick={newDraft} disabled={busy}>
                                            {busy ? 'Starting…' : `Start Draft ${(latest?.versionNumber ?? version.versionNumber) + 1} →`}
                                        </button>
                                    ) : (latest && latest.id !== version.id && (
                                        <button className='essayEd__btn essayEd__btn--primary' onClick={() => switchVersion(latest.id)}>
                                            Open {latest.label} →
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className={compareOn && priorVersion && priorContent ? 'essayEd__compareGrid' : undefined}>
                            {compareOn && priorVersion && priorContent && (
                                <div className='essayEd__page essayEd__page--prior'>
                                    <div className='essayEd__pageBar'>
                                        <span>{priorVersion.label} · reviewed</span>
                                        <span className='essayEd__compareHint'>Tutor comments highlighted</span>
                                    </div>
                                    <div className='essayEd__manuscript'>
                                        {buildSegments(priorContent, priorComments).map((seg, i) => (
                                            seg.comment
                                                ? (
                                                    <span
                                                        key={i}
                                                        data-cid={seg.comment.id}
                                                        className={`essayEd__hl ${hoverCommentId === seg.comment.id ? 'essayEd__hl--hover' : ''}`}
                                                        title={seg.comment.body}
                                                        onMouseEnter={() => setHoverCommentId(seg.comment!.id)}
                                                        onMouseLeave={() => setHoverCommentId(null)}
                                                    >{seg.text}</span>
                                                )
                                                : <span key={i}>{seg.text}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className='essayEd__page'>
                                <div className='essayEd__pageBar'>
                                    {editable ? (
                                        <span className={`essayEd__progress ${overLimit ? 'essayEd__progress--over' : ''}`}>
                                            {target
                                                ? <>
                                                    <span className='essayEd__track'><span className='essayEd__fill' style={{ width: `${pct}%` }} /></span>
                                                    <b>{count}</b> / {target} words
                                                </>
                                                : <><b>{count}</b> words</>}
                                        </span>
                                    ) : (
                                        <span className={target && version.wordCount > target ? 'essayEd__progress--over' : ''}>
                                            {version.wordCount}{target ? ` / ${target}` : ''} words · {version.label}
                                        </span>
                                    )}
                                    <SaveBadge status={status} state={state} />
                                </div>

                                {editable ? (
                                    <div
                                        className='essayEd__editWrap'
                                        onMouseMove={onEditorMouseMove}
                                        onMouseLeave={() => setHoverCommentId(null)}
                                    >
                                        <div className='essayEd__manuscript essayEd__backdrop' ref={backdropRef} aria-hidden='true'>
                                            {backdropSegs.map((seg, i) => (seg.range
                                                ? <mark key={i} className={`essayEd__bhl ${hoverCommentId === seg.range.id ? 'essayEd__bhl--hover' : ''}`} data-hl={seg.range.id}>{seg.text}</mark>
                                                : <span key={i}>{seg.text}</span>
                                            ))}
                                            {'\n'}
                                        </div>
                                        <textarea
                                            ref={taRef}
                                            className='essayEd__manuscript essayEd__textarea'
                                            value={content}
                                            onChange={e => onChangeContent(e.target.value)}
                                            placeholder='Write your essay here…'
                                            spellCheck
                                        />
                                    </div>
                                ) : (
                                    <div className='essayEd__manuscript'>
                                        {buildSegments(version.content, state === 'reviewed' ? comments : []).map((seg, i) => (
                                            seg.comment ? (
                                                <span
                                                    key={i}
                                                    data-cid={seg.comment.id}
                                                    className={`essayEd__hl ${hoverCommentId === seg.comment.id ? 'essayEd__hl--hover' : ''}`}
                                                    onMouseEnter={() => setHoverCommentId(seg.comment!.id)}
                                                    onMouseLeave={() => setHoverCommentId(null)}
                                                    title={seg.comment.body}
                                                    onClick={() => {
                                                        const el = document.getElementById(`note-${seg.comment!.id}`);
                                                        scrollWindowTo(el);
                                                    }}
                                                >{seg.text}</span>
                                            ) : <span key={i}>{seg.text}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            </div>{/* /compareGrid (or plain wrapper) */}
                        </>
                    )}
                </div>

                {/* right rail */}
                <aside>
                    {/* draft picker — dropdown replaces the old drafts rail */}
                    <div className='essayEd__draftPick' ref={draftPickRef}>
                        <div className='essayEd__draftPickRow'>
                            <button type='button' className='essayEd__ddBtn' onClick={() => setDraftsOpen(o => !o)}>
                                <span className='essayEd__ddLabel'>{version?.label ?? 'Drafts'}</span>
                                {version && state === 'editing' && <span className='essayEd__pill essayEd__pill--edit'>EDITING</span>}
                                {version && state === 'in_review' && <span className='essayEd__pill essayEd__pill--review'>IN REVIEW</span>}
                                {version && state === 'reviewed' && (
                                    <span className='essayEd__cmtChip'><MdOutlineChatBubbleOutline size={11} /> {comments.length}</span>
                                )}
                                <MdKeyboardArrowDown size={19} className={`essayEd__ddArrow ${draftsOpen ? 'essayEd__ddArrow--up' : ''}`} />
                            </button>
                        </div>
                        {draftsOpen && (
                            <div className='essayEd__ddList'>
                                {versions.map(v => {
                                    const vs = draftState(v);
                                    const on = v.id === activeVersionId;
                                    return (
                                        <button
                                            key={v.id}
                                            type='button'
                                            className={`essayEd__draft ${on ? 'essayEd__draft--on' : ''}`}
                                            onClick={() => { setDraftsOpen(false); if (!on) switchVersion(v.id); }}
                                        >
                                            <span className='essayEd__draftName'>
                                                {v.label}
                                                {vs === 'editing' && <span className='essayEd__pill essayEd__pill--edit'>EDITING</span>}
                                                {vs === 'in_review' && <span className='essayEd__pill essayEd__pill--review'>IN REVIEW</span>}
                                                {vs === 'reviewed' && (
                                                    <span className='essayEd__cmtChip'><MdOutlineChatBubbleOutline size={11} /> {v.commentCount}</span>
                                                )}
                                            </span>
                                            <span className='essayEd__draftMeta'>
                                                {shortDate(v.createdAt)} · {v.wordCount} words
                                                {v.commentCount > 0 ? ` · ${v.commentCount} comment${v.commentCount === 1 ? '' : 's'}` : ''}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {draftError && <div className='essayEd__draftError'>{draftError}</div>}
                    </div>

                    {version && state === 'editing' && (
                        priorComments.length > 0 ? (
                            <>
                                <div className='essayEd__railHead'>
                                    <h3>Feedback to address</h3>
                                    <span className='essayEd__railCount'>{priorComments.length}</span>
                                </div>
                                <p className='essayEd__railIntro'>
                                    Notes your tutor left on <b>{priorVersion?.label}</b>. Address them as you revise —
                                    submitting this draft marks them all as resolved.
                                </p>
                                {[...priorComments].sort((a, b) => (
                                    a.isGeneral !== b.isGeneral
                                        ? Number(a.isGeneral) - Number(b.isGeneral)
                                        : a.anchorStart - b.anchorStart
                                )).map(c => {
                                    // General comments have no anchor: no quote, no "show me where",
                                    // no "text edited" note — just the body.
                                    const inDraft = !c.isGeneral && !!c.anchorText && content.indexOf(c.anchorText) !== -1;
                                    return (
                                        <div
                                            key={c.id}
                                            className={`essayEd__note ${hoverCommentId === c.id ? 'essayEd__note--hover' : ''} ${!c.isGeneral && c.anchorText ? 'essayEd__note--clickable' : ''}`}
                                            onMouseEnter={() => !c.isGeneral && setHoverCommentId(c.id)}
                                            onMouseLeave={() => setHoverCommentId(null)}
                                            onClick={() => scrollToPassage(c)}
                                        >
                                            <div className='essayEd__noteMeta'>
                                                <span className='essayEd__noteWho'>{initials(c.authorName)}</span>
                                                <span className='essayEd__noteAuthor'>{c.authorName || 'Tutor'}</span>
                                                <span className='essayEd__noteTime'>{shortDateTime(c.createdAt)}</span>
                                            </div>
                                            {c.isGeneral
                                                ? <span className='essayEd__noteChip'>Overall feedback</span>
                                                : c.anchorText && <span className='essayEd__noteAnchor'>"{c.anchorText}"</span>}
                                            <div className='essayEd__noteBody'>{c.body}</div>
                                            {!c.isGeneral && !inDraft && c.anchorText && (
                                                <div className='essayEd__noteFoot'>
                                                    <span className='essayEd__checkNote'>text edited in this draft</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        ) : (
                            <>
                                <div className='essayEd__railHead'><h3>Drafting</h3></div>
                                <p className='essayEd__railIntro'>
                                    Write freely, then <b>submit this draft</b> when it's ready. Your tutor's comments will show up here
                                    after they review it.
                                </p>
                            </>
                        )
                    )}

                    {version && state === 'in_review' && (
                        <>
                            <div className='essayEd__railHead'><h3>Status</h3></div>
                            <div className='essayEd__statusline'>
                                <span className='essayEd__tick'><MdCheck size={13} /></span> Submitted {version.submittedAt ? shortDate(version.submittedAt) : ''}
                            </div>
                            <p className='essayEd__railIntro'>
                                Your tutor will review <b>{version.label}</b> and leave comments here. You'll be notified when feedback is ready.
                            </p>
                        </>
                    )}

                    {version && state === 'reviewed' && (
                        <>
                            <div className='essayEd__railHead'>
                                <h3>Tutor comments</h3>
                                <span className='essayEd__railCount'>{comments.length}</span>
                            </div>
                            {comments.length === 0 ? (
                                <p className='essayEd__railIntro'>This draft was reviewed but has no inline comments.</p>
                            ) : [...comments].sort((a, b) => (
                                a.isGeneral !== b.isGeneral
                                    ? Number(a.isGeneral) - Number(b.isGeneral)
                                    : a.anchorStart - b.anchorStart
                            )).map(c => (
                                <div
                                    key={c.id}
                                    id={`note-${c.id}`}
                                    className={`essayEd__note ${hoverCommentId === c.id && !c.isGeneral ? 'essayEd__note--hover' : ''} ${!c.isGeneral && c.anchorText ? 'essayEd__note--clickable' : ''}`}
                                    onMouseEnter={() => !c.isGeneral && setHoverCommentId(c.id)}
                                    onMouseLeave={() => setHoverCommentId(null)}
                                    onClick={() => scrollToPassage(c)}
                                >
                                    <div className='essayEd__noteMeta'>
                                        <span className='essayEd__noteWho'>{initials(c.authorName)}</span>
                                        <span className='essayEd__noteAuthor'>{c.authorName || 'Tutor'}</span>
                                        <span className='essayEd__noteTime'>{shortDateTime(c.createdAt)}</span>
                                    </div>
                                    {c.isGeneral
                                        ? <span className='essayEd__noteChip'>Overall feedback</span>
                                        : c.anchorText && <span className='essayEd__noteAnchor'>"{c.anchorText}"</span>}
                                    <div className='essayEd__noteBody'>{c.body}</div>

                                </div>
                            ))}
                        </>
                    )}
                </aside>
            </div>
        </div>
    );
}

function initials(name: string | null): string {
    if (!name) return 'T';
    return name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function SaveBadge({ status, state }: { status: SaveStatus; state: DraftState }) {
    if (state === 'in_review') return <span className='essayEd__save essayEd__save--ro'><span className='essayEd__saveDot' />In review</span>;
    if (state === 'reviewed') return <span className='essayEd__save essayEd__save--ro'><span className='essayEd__saveDot' />Read-only</span>;
    if (status === 'saving') return <span className='essayEd__save essayEd__save--saving'>Saving…</span>;
    if (status === 'saved') return <span className='essayEd__save'><span className='essayEd__saveDot' />Saved · editing</span>;
    if (status === 'error') return <span className='essayEd__save essayEd__save--error'>Couldn't save — retrying on next edit</span>;
    if (status === 'locked') return <span className='essayEd__save essayEd__save--ro'><span className='essayEd__saveDot' />Read-only</span>;
    return <span className='essayEd__save'><span className='essayEd__saveDot' />Saved · editing</span>;
}

export default EssayEditor;
